import { createContext, useContext, useCallback, useState, useEffect } from "react";
import { api } from "../../../services/api";
import { Topic } from "../Blog.types";
import { ShowToast } from "../../../services/toast";

type GetTopicsParams = {
  page?: number;
  limit?: number;
  keyword?: string;
  sort?: keyof Topic;
  sortAsc?: boolean;
};

type TopicContextType = {
  topics: Topic[];
  total: number;
  loading: boolean;
  fetchTopics: (params?: GetTopicsParams) => Promise<void>;
  createTopic: (topic: Topic) => Promise<void>;
  updateTopic: (id: string, topic: Topic) => Promise<void>;
  deleteTopic: (id: string) => Promise<void>;
};

const TopicContext = createContext<TopicContextType | undefined>(undefined);

export function TopicProvider({ children }: { children: React.ReactNode }) {
  const [topics, setTopics] = useState<Topic[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchTopics = useCallback(async (params?: GetTopicsParams) => {
    try {
      setLoading(true);
      const response = await api.getTopics({
        page: params?.page,
        limit: params?.limit,
        keyword: params?.keyword,
      });
      setTopics(response.topics);
      setTotal(response.total);
    } catch (error: any) {
      ShowToast({
        type: "error",
        message: error?.response?.data?.message || "Failed to fetch tags"
      });
    } finally {
      setLoading(false);
    }
  }, []);

  const createTopic = useCallback(async (topic: Topic) => {
    try {
      setLoading(true);
      await api.createTopic(topic);
      await fetchTopics();
      ShowToast({
        type: "success",
        message: "Tag created successfully"
      });
    } catch (error: any) {
      ShowToast({
        type: "error",
        message: error?.response?.data?.message || "Failed to create tag"
      });
      throw error;
    } finally {
      setLoading(false);
    }
  }, [fetchTopics]);

  const updateTopic = useCallback(async (id: string, topic: Topic) => {
    try {
      setLoading(true);
      await api.updateTopic(id, topic);
      await fetchTopics();
      ShowToast({
        type: "success",
        message: "Tag updated successfully"
      });
    } catch (error: any) {
      ShowToast({
        type: "error",
        message: error?.response?.data?.message || "Failed to update tag"
      });
      throw error;
    } finally {
      setLoading(false);
    }
  }, [fetchTopics]);

  const deleteTopic = useCallback(async (id: string) => {
    try {
      setLoading(true);
      await api.deleteTopic(id);
      await fetchTopics();
      ShowToast({
        type: "success",
        message: "Tag deleted successfully"
      });
    } catch (error: any) {
      ShowToast({
        type: "error",
        message: error?.response?.data?.message || "Failed to delete tag"
      });
      throw error;
    } finally {
      setLoading(false);
    }
  }, [fetchTopics]);

  useEffect(() => {
    fetchTopics();
  }, []);

  return (
    <TopicContext.Provider
      value={{
        topics,
        total,
        loading,
        fetchTopics,
        createTopic,
        updateTopic,
        deleteTopic,
      }}
    >
      {children}
    </TopicContext.Provider>
  );
}

export function useTopics() {
  const context = useContext(TopicContext);
  if (context === undefined) {
    throw new Error("useTopics must be used within a TopicProvider");
  }
  return context;
}
